import {responseContent} from './http';
import {defaults} from 'lodash';
import {HttpResponseMessage} from "aurelia-http-client";

export type PaginatorData = {
  pageNum: number
  pageSize: number
} & {
  [key: string]: any
}

export type PaginatorValue<T> = {
  cancel?: () => void,
  promise: Promise<T[]>
}

export type PaginatorResponseContent<T> = T[] | { payload: T[] }

export type PaginatorResponse<T> = HttpResponseMessage & {
  content: PaginatorResponseContent<T>
}

export type PaginatorExtractor<T> = (data: PaginatorResponseContent<T>) => T[]

export type PaginatorSupplier<T> = (data: PaginatorData) => PromiseWithCancel<PaginatorResponse<T>>

export class Paginator<T> {

  private __pageNum: number = -1;
  private __pageSize: number = -1;
  private __canGetMore: boolean = true;
  private __pendingValue: PaginatorValue<T>;
  private __pendingPageNum: number = -1;
  private __promiseSupplier: PaginatorSupplier<T>;
  private __paginator: Generator<PaginatorValue<T>, any, any>;
  private __dataExtractor: PaginatorExtractor<T>;

  constructor(data: PaginatorData, promiseSupplier: PaginatorSupplier<T>, dataExtractor: PaginatorExtractor<T> = extractPayload) {
    defaults(data, {
      pageNum: 1,
      pageSize: 10
    });

    this.__dataExtractor = dataExtractor;
    this.__promiseSupplier = promiseSupplier;
    this.__paginator = this.__makePaginator(data);
  }

  get pageNum() {
    return Math.max(this.__pageNum - 1, -1);
  }

  async next() {
    const {done} = this.__paginator.next();
    return done ? [] : await this.__pendingValue.promise;
  }

  cancel() {
    if (this.__pendingValue && this.__pendingValue.cancel) {
      this.__pendingValue.cancel();
      this.__pendingValue = null;
    }
  }

  private *__makePaginator(data: PaginatorData) {
    this.__pageNum = data.pageNum;
    this.__pageSize = data.pageSize;

    while (this.__canGetMore) {
      if (this.__pendingPageNum === this.__pageNum && this.__pendingValue) {
        yield this.__pendingValue;
      } else {
        this.__pendingPageNum = this.__pageNum;
        const {cancel, promise} = this._makeRequest(defaults({
          pageNum: this.__pageNum,
          pageSize: this.__pageSize
        }, data));

        const itemsPromise = promise
          .then(d => this.__handleResponse(d))
          .catch(e => this.__handleError(e));

        yield this.__pendingValue = {cancel, promise: itemsPromise};
      }
    }

    return {};
  }

  private __handleResponse(data: PaginatorResponseContent<T>) {
    const extracted = this.__dataExtractor(data);
    const dataSize = extracted.length !== undefined ? extracted.length : (data as any).dataSize;

    this.__canGetMore = dataSize >= this.__pageSize;
    this.__pageNum++;

    return extracted;
  }

  private __handleError(error): never {
    this.__pendingValue = null;
    throw error;
  }

  private _makeRequest(data: PaginatorData): {cancel: () => void, promise: Promise<PaginatorResponseContent<T>>} {
    const promise = this.__promiseSupplier(data);
    return {cancel: promise.cancel, promise: responseContent(promise)};
  }
}

function extractPayload<T>(data: PaginatorResponseContent<T>) {
  if ('payload' in data) {
    return data.payload;
  }
  return data;
}
